import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import Disclaimer from '../components/Disclaimer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[var(--sd-bg)] flex flex-col">
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-1">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Back Button */}
          <Link to="/">
            <Button variant="ghost" className="mb-6 text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)]">
              <ArrowLeft size={16} className="mr-2" />
              Back to Home
            </Button>
          </Link>

          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <Shield size={32} className="text-[var(--sd-primary)]" />
            <div>
              <h1 className="text-4xl font-semibold text-[var(--sd-fg)]">Privacy Policy</h1>
              <p className="text-[var(--sd-muted-fg)] mt-2">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-invert max-w-none space-y-8 text-[var(--sd-fg)]">
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">1. Introduction</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                Welcome to Selah Downloads ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our spiritual insights platform at www.selahdownloads.com.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">2.1 Personal Information</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">
                We collect the following personal information when you create an account:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Password (encrypted)</li>
                <li>Account creation date</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">2.2 Spiritual Download Content</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                Your spiritual downloads, insights, prayers, and reflections are <strong>encrypted client-side</strong> before being stored. We use your email address to derive an encryption key, ensuring that only you can decrypt and access your personal spiritual content.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">2.3 Usage Data</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                We may collect information about how you access and use our platform, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Device information</li>
                <li>Browser type</li>
                <li>IP address</li>
                <li>Usage patterns and features accessed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">3. How We Use Your Information</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Provide and maintain our service</li>
                <li>Generate AI-powered spiritual insights and prayers</li>
                <li>Authenticate your account and ensure security</li>
                <li>Send you confirmations, updates, and administrative messages</li>
                <li>Improve and personalize your experience</li>
                <li>Respond to your requests and support needs</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">4. Data Security & Encryption</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">
                <strong>Your privacy is paramount.</strong> We implement industry-standard security measures:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li><strong>Client-Side Encryption:</strong> Your spiritual downloads are encrypted on your device before being sent to our servers</li>
                <li><strong>Password Security:</strong> All passwords are hashed using bcrypt with salt</li>
                <li><strong>HTTPS:</strong> All data transmission is encrypted using TLS/SSL</li>
                <li><strong>Limited Access:</strong> Only you can decrypt and view your personal downloads</li>
                <li><strong>No Third-Party Sharing:</strong> We never sell or share your personal spiritual content with third parties</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">5. AI Processing &amp; Third-Party AI Disclosure</h2>

              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">5.1 What we disclose (Apple App Store 5.1.1(i) / 5.1.2(i))</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">
                Selah Downloads uses a third-party artificial intelligence service, <strong>OpenAI</strong>, to transform your voice reflections into structured spiritual guidance. We disclose this <em>before</em> any data is sent and require your explicit consent (via the in-app consent modal on first launch, and manageable at any time in Settings → Privacy &amp; AI).
              </p>

              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">5.2 Exactly what data is sent to OpenAI</h3>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4 mb-4">
                <li><strong>Voice recordings</strong> that you capture inside the app (used for speech-to-text transcription).</li>
                <li><strong>Transcribed reflection text</strong> (used to categorize the reflection, match it to Scripture, generate an activation prayer, and detect patterns across your journey).</li>
              </ul>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">
                We do <strong>not</strong> send: your name, email, IP address, precise location, contacts, photos, health data, financial data, or the encrypted contents of prior journal entries.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">5.3 Who receives the data</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">
                <strong>OpenAI, L.L.C.</strong> — 3180 18th Street, San Francisco, CA 94110, United States. Our relationship with OpenAI is governed by their API Services Agreement and Enterprise Data Processing Agreement, which OpenAI publishes at{' '}
                <a
                  href="https://openai.com/policies"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-[var(--sd-primary)]"
                >
                  openai.com/policies
                </a>
                .
              </p>

              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">5.4 What OpenAI does with the data (equal-protection commitment)</h3>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4 mb-4">
                <li>Processes the data solely to return AI-generated results to Selah.</li>
                <li>Does <strong>not</strong> use your submissions to train, improve, or fine-tune OpenAI's models.</li>
                <li>Retains submitted data for up to 30 days for abuse and safety monitoring, then permanently deletes it.</li>
                <li>Is contractually bound by an Enterprise Data Processing Agreement providing protections equivalent to those in this Privacy Policy.</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">5.5 Your explicit consent + revocation rights</h3>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4 mb-4">
                <li>The first time you open Selah, a blocking modal shows this exact disclosure and asks whether you agree.</li>
                <li>If you <strong>Decline</strong>, no data is ever sent to OpenAI. Voice capture continues to work locally (audio saved to your device), but AI-generated insights, Scripture matching, and prayer generation are disabled.</li>
                <li>If you <strong>Agree</strong>, we record the exact consent version and timestamp on your account, and you may revoke it at any time under <strong>Settings → Privacy &amp; AI → Revoke AI consent</strong>. Revocation is instant and blocks all future AI processing server-side.</li>
                <li>Consent is per-user (not per-device). Revoking on one device revokes everywhere.</li>
                <li>If we materially change what data is sent or who receives it, we will re-prompt you for consent before continuing AI processing.</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">5.6 What Selah stores after AI processing</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                Once OpenAI returns a result, Selah stores only the <strong>final analysis</strong> (category, themes, insights, Scripture reference, and prayer text) attached to your download, encrypted at rest. The raw audio is retained only if you have explicitly enabled audio archival in Settings; otherwise it is discarded once transcription completes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">5A. Ambient Capture &amp; Third-Party Integrations</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-3">
                If you enable <strong>Ambient Capture</strong> (Siri Shortcuts, IFTTT, Zapier, Alexa Routines), the transcribed text you dictate is transmitted to our servers via a capture token you generate. Because these captures arrive without your device being unlocked, we cannot encrypt them client-side at the moment of arrival.
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li><strong>Plaintext holding:</strong> Ambient captures are stored on our servers in plaintext until you open the app, review them, and press &quot;Save to Journey&quot; — at which point they are encrypted client-side just like your other downloads.</li>
                <li><strong>Retention limit:</strong> Ambient captures you neither promote nor discard are automatically purged after 60 days.</li>
                <li><strong>Third-party responsibility:</strong> Any content routed through Siri, IFTTT, Zapier, or Alexa is also subject to those providers&apos; privacy policies. We do not control what they log.</li>
                <li><strong>Revocation:</strong> You can revoke any capture token at any time from Settings, immediately stopping new captures from that source.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">5B. Subprocessors</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-3">
                We use a small number of trusted subprocessors to deliver the service:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li><strong>OpenAI</strong> — AI analysis of the text you submit (categorization, insights, Scripture matching, prayer generation).</li>
                <li><strong>Emergent</strong> — hosting, database, and API infrastructure.</li>
                <li><strong>MongoDB Atlas</strong> — encrypted database at rest.</li>
                <li><strong>Resend</strong> (optional) — transactional email (welcome, password reset).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">6. Data Retention &amp; Account Deletion</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-3">
                We retain your personal information and encrypted downloads for as long as your account is active. You may delete your account at any time via:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Inside the app: <strong>Settings → Danger Zone → Delete Account</strong></li>
                <li>Without signing in: <a href="/delete-account" className="text-[var(--sd-primary)] hover:underline">selahdownloads.com/delete-account</a></li>
              </ul>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mt-3">
                Deletion permanently removes your profile, every download, every ambient capture, and every follow-up. Database backups are rotated within 30 days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">7. Your Rights</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Access your personal information</li>
                <li>Update or correct your information (via Settings)</li>
                <li>Delete your account and all associated data (see §6 above)</li>
                <li>Export your data (via the Export &amp; Share menu inside each analysis)</li>
                <li>Opt-out of promotional communications</li>
                <li>Lodge a complaint with your local data-protection authority (for EU/UK residents under GDPR)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">8. Cookies &amp; Tracking</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                We use localStorage and authentication tokens to maintain your logged-in state and remember your theme preference. We do not use third-party advertising cookies, analytics trackers, or tracking pixels.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">9. Children&apos;s Privacy</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                Selah Downloads is intended for users <strong>13 years of age or older</strong> (or the minimum digital-consent age in your country). We confirm this at signup via an age checkbox. We do not knowingly collect personal information from children under 13. If we learn we have collected information from a child under 13, we will delete it promptly. Parents or guardians who believe their child has provided us information may contact <a href="mailto:privacy@selahdownloads.com" className="text-[var(--sd-primary)] hover:underline">privacy@selahdownloads.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">10. Changes to This Policy</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">11. Contact Us</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                If you have any questions about this Privacy Policy or our practices, please contact us at:
              </p>
              <p className="leading-relaxed text-[var(--sd-primary)] mt-4">
                <strong>Email:</strong> privacy@selahdownloads.com<br />
                <strong>Website:</strong> www.selahdownloads.com
              </p>
              <address
                className="not-italic leading-relaxed text-[var(--sd-muted-fg)] mt-4 text-sm"
                data-testid="privacy-business-address"
              >
                <strong className="text-[var(--sd-fg-secondary)]">Mailing address:</strong><br />
                Selah &amp; Stone Legacy Holdings LLC<br />
                5005 W. Laurel, Ste. 100<br />
                Tampa, FL 33607, USA
              </address>
            </section>
          </div>
        </motion.div>
      </div>
      <Disclaimer />
    </div>
  );
};

export default PrivacyPolicy;
